import { RefObject, useCallback, useMemo } from 'react';

import {
  GiftAddressRegion,
  GiftDeliveryFeePreviewRequest,
} from '@shared/api/giftOrder/giftOrder.interface';
import { formatPrice } from '@shared/utils/formatPrice';

import { scrollToInvalidField } from '../utils/scrollToInvalidField';
import {
  GiftDeliveryFeePreviewState,
  useGiftDeliveryFeePreview,
} from './useGiftDeliveryFeePreview';

interface Params {
  /** 주문 라인 (giftItem id·수량·옵션) — 수량/옵션이 바뀌면 소계가 바뀌어 다시 묻는다 */
  items: GiftDeliveryFeePreviewRequest['orderDetails'];
  /** 카카오 응답 원문 — 주소 검색 완료 시 갱신. 없으면(구버전 저장 주소) 서버가 ADDRESS_INCOMPLETE */
  region: GiftAddressRegion | undefined;
  /** 배송형 상품 포함 + 일괄배송(Bulk) 선택 상태 */
  isBulkDelivery: boolean;
  /** 배송지 기본주소 입력값 — 비어 있으면 미리보기를 하지 않는다 */
  receiverAddress: string;
  /** 주소 검색 버튼 ref — 결제 차단 alert 뒤 해당 위치로 스크롤 */
  addressButtonRef: RefObject<HTMLButtonElement>;
}

export interface GiftCheckoutDeliveryFee {
  /** 미리보기 상태 원본 — GiftDeliveryModeSelector·GiftDeliveryFeeSummaryRow에 그대로 전달 */
  deliveryFee: GiftDeliveryFeePreviewState;
  /**
   * 주문 DTO의 deliveryFee — 권역 브랜드이고 배송 가능일 때만 미리보기 total 그대로(0원=무료여도 `{ total: 0 }`),
   * 권역 정책이 없는 브랜드(applicable=false)·배송 불가·미조회는 undefined(필드 생략).
   */
  deliveryFeeDto: { total: number } | undefined;
  /** 결제창 수단 제한 — 권역 브랜드 응답의 allowedPaymentMethods, 그 외 null(제한 없음) */
  allowedPaymentMethods: string[] | null;
  /**
   * 일괄배송 배송지 유효성 — 주소 미입력 / 권역 배송 불가·주소 불완전·확인 중이면 alert + 스크롤 후 false.
   * useGiftCheckoutPayment의 validateDeliveryAddress로 넘긴다
   */
  validateDeliveryAddress: () => boolean;
  /** 하단 결제 버튼 라벨 (진행 중 → 배송비 확인 중 → 배송지 확인 요청 → "N원 결제하기") */
  payButtonLabel: (finalTotal: number, isProcessing: boolean) => string;
}

/**
 * 체크아웃 화면(장바구니·바로구매) 공용 — 권역 배송비 미리보기 + 주문 DTO 배송비 조립 + 결제 진입 검증 + 버튼 라벨.
 * 두 페이지가 같은 판정·같은 문구를 쓰도록 한 곳에 둔다. 금액 계산(calcGiftOrderPayment)에는 `deliveryFee.feeTotal`을 쓴다.
 */
export const useGiftCheckoutDeliveryFee = ({
  items,
  region,
  isBulkDelivery,
  receiverAddress,
  addressButtonRef,
}: Params): GiftCheckoutDeliveryFee => {
  // 일괄배송 + 주소 입력 뒤에만 서버에 묻는다
  const deliveryFee = useGiftDeliveryFeePreview({
    items,
    region,
    enabled: isBulkDelivery && !!receiverAddress.trim(),
  });
  const { preview, loading, blocksPayment } = deliveryFee;

  const deliveryFeeDto = useMemo(
    () =>
      preview?.applicable && preview.deliverable ? { total: preview.total } : undefined,
    [preview],
  );

  const allowedPaymentMethods = preview?.applicable ? preview.allowedPaymentMethods : null;

  // 훅 반환 객체 전체가 아니라 실제 쓰는 필드만 deps — 매 렌더 재생성 방지
  const validateDeliveryAddress = useCallback(() => {
    if (!isBulkDelivery) return true;
    if (!receiverAddress.trim()) {
      alert('배송지 주소를 입력해주세요.');
      scrollToInvalidField(addressButtonRef.current);
      return false;
    }
    // 권역 배송비 대상 — 배송 불가/주소 불완전/확인 중이면 결제 진입 차단 (서버도 400을 내지만 여기서 먼저 안내)
    if (blocksPayment) {
      alert(
        preview?.applicable && !preview.deliverable
          ? preview.message
          : '배송비를 확인하는 중입니다. 잠시 후 다시 시도해주세요.',
      );
      scrollToInvalidField(addressButtonRef.current);
      return false;
    }
    return true;
  }, [isBulkDelivery, receiverAddress, blocksPayment, preview, addressButtonRef]);

  const payButtonLabel = useCallback(
    (finalTotal: number, isProcessing: boolean) =>
      isProcessing
        ? '결제 진행 중...'
        : blocksPayment && loading
        ? '배송비 확인 중...'
        : blocksPayment
        ? '배송지를 확인해주세요'
        : `${formatPrice(finalTotal)}원 결제하기`,
    [blocksPayment, loading],
  );

  return {
    deliveryFee,
    deliveryFeeDto,
    allowedPaymentMethods,
    validateDeliveryAddress,
    payButtonLabel,
  };
};
